import React, { useState, useEffect } from "react";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { CustomButton } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { Cancel, AddCircleTwoTone } from "@material-ui/icons";
import {
  paymentOptions,
  shiftGradeOptions,
  CustomBox,
  cardStyle,
  infoMessageStyle,
  FullShiftCreditExample,
  PartialShiftCreditExample,
  WorkingHoursExample,
} from "../Component/ShiftCategoryOptions";
import {
  Box,
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  TableContainer,
  TableCell,
  Tooltip,
  TableHead,
  IconButton,
  Grid,
  TextField,
  TableBody,
  Table,
  TableRow,
} from "@mui/material";
import { themes, tableHeaderSx, tableRowSx } from "services/constants";

export const BasicInfo = ({
  onUpdate,
  handleNext,
  data,
  isEditScreen,
  showCategoryName,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...data,
      payment_based_on: "Full Shift Credit",
      is_partial_shift_work: false,
      is_include_sft_grade_sfts: "Shift Hours",
      additional_percentage: 0,
      shift_count: 0,
      rows: [{ work_min_percentage: "", count_as_shift: "" }],
    },
  });

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [rows, setRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [rowError, setRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);

  const updateShiftCategoryName = (event) => {
    setValue("sft_category_name", event.target.value);
  };

  const hanldePaymentBasedOn = (event) => {
    setValue("payment_based_on", event.target.value);
    setValue("is_partial_shift_work", false);
    setRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setRowError([{ work_min_percentage: false, count_as_shift: false }]);
    setValue("additional_percentage", 0);
    setValue("shift_count", 0);
  };

  const handleSftGradeChange = (event) => {
    setValue("is_include_sft_grade_sfts", event.target.value);
  };

  const validateRows = () => {
    const newErrors = rows.map((row, index) => {
      const minEmpty = row.work_min_percentage === "";
      const compEmpty = row.count_as_shift === "";

      let compInvalid = false;
      let minInvalid = false;

      if (!compEmpty && index > 0) {
        const current = parseFloat(row.count_as_shift);
        const previous = parseFloat(rows[index - 1].count_as_shift);

        if (!isNaN(current) && !isNaN(previous)) {
          const isPrevInteger = Number.isInteger(previous);
          if (isPrevInteger) {
            compInvalid = current < previous;
          } else {
            compInvalid = current <= previous;
          }
        } else {
          compInvalid = true;
        }
      }

      if (!minEmpty && index > 0) {
        const currentMin = parseFloat(row.work_min_percentage);
        const prevMin = parseFloat(rows[index - 1].work_min_percentage);
        minInvalid =
          isNaN(currentMin) || isNaN(prevMin) || currentMin <= prevMin;
      }

      return {
        work_min_percentage: minEmpty || minInvalid,
        count_as_shift: compEmpty || compInvalid,
      };
    });

    const hasError = newErrors.some(
      (err) => err.work_min_percentage || err.count_as_shift
    );

    setRowError(newErrors);
    return !hasError;
  };

  const handleAddRow = () => {
    const isValid = validateRows();
    if (!isValid) return;

    setRows((prev) => [
      ...prev,
      { work_min_percentage: "", count_as_shift: "" },
    ]);
    setRowError((prev) => [
      ...prev,
      { work_min_percentage: false, count_as_shift: false },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowError((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);

    const updatedErrors = [...rowError];
    if (!updatedErrors[index])
      updatedErrors[index] = {
        work_min_percentage: false,
        count_as_shift: false,
      };
    updatedErrors[index][field] = false;
    setRowError(updatedErrors);
  };

  const onSubmit = async (formValues) => {
    const isValid =
      watch("payment_based_on") === "Partial Shift Credit"
        ? validateRows()
        : true;
    if (isValid) {
      onUpdate({
        ...formValues,
        rows,
      });
      handleNext(1);
    }
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
      const mappedRows = data?.rows?.map((item) => ({
        work_min_percentage: item.work_min_percentage,
        count_as_shift: item.count_as_shift,
      }));
      setRows(mappedRows);
    }
  }, [data, reset]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Shift Category Name{" "}
              <span style={{ color: themes?.redColor }}>*</span>
            </Typography>
            <CustomTextBox
              selectedValue={watch("sft_category_name")}
              rules={{
                ...register("sft_category_name", {
                  required: "Shift category name is required",
                  minLength: {
                    value: 3,
                    message: "Minium length is 3 characters",
                  },
                  maxLength: {
                    value: 100,
                    message: "Maximum length is 100 characters",
                  },
                  validate: {
                    atleastOneCharacterWithSpaceReg: (value) => {
                      if (
                        atleastOneCharacterWithSpace.test(value) ||
                        value === ""
                      ) {
                        return true;
                      } else {
                        return "Atleast one alphabet required";
                      }
                    },

                    checkShiftUniqueName: async (fieldValue) => {
                      const editUniqueCheck =
                        fieldValue !== "" &&
                        watch("sft_category_name") !== data?.sftCategoryName;
                      if (isEditScreen ? editUniqueCheck : fieldValue !== "") {
                        const response = await fetch(
                          `${domain}unique/shift-category/?name=${fieldValue.trim()}`,
                          {
                            method: "GET",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `token ${token}`,
                            },
                          }
                        );
                        const resp = await response.json();
                        if (resp.exists) {
                          return "Shift category name already exists";
                        }
                      }
                    },
                  },
                }),
              }}
              updateDataFunction={updateShiftCategoryName}
              placeHolder="Enter shift category name"
              errorMessage={
                errors?.sft_category_name?.message
                  ? errors?.sft_category_name?.message
                  : ""
              }
              textBoxError={errors.sft_category_name ? true : false}
            />
          </Box>
          {showCategoryName && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Cloned From :
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ color: themes.stepActiveColor, fontWeight: 600 }}
              >
                {data?.sftCategoryName}
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: themes?.miliedLightGray }} />

          <Box sx={{ ml: 1 }}>
            <Typography fontWeight="medium">Payment Based On</Typography>
            <Typography variant="body2" color="text.secondary">
              Define the payment calculation method with this option.
            </Typography>
          </Box>

          <Box>
            <RadioGroup
              row
              value={watch("payment_based_on")}
              onChange={hanldePaymentBasedOn}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                ml: 0.8,
              }}
            >
              {paymentOptions.map((opt) => {
                const isActive = watch("payment_based_on") === opt.id;
                return (
                  <Box key={opt.id} sx={cardStyle(isActive)}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Radio value={opt.id} />
                      <Typography
                        fontWeight={"medium"}
                        sx={{
                          color:
                            watch("payment_based_on") === opt.id
                              ? themes?.stepActiveColor
                              : "inherit",
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        marginLeft: "32px",
                        maxWidth: 250,
                        wordBreak: "break-word",
                      }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>

          {watch("payment_based_on") === "Full Shift Credit" && (
            <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
              <FullShiftCreditExample />
            </Box>
          )}

          {watch("payment_based_on") === "Hours" && (
            <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
              <WorkingHoursExample />
            </Box>
          )}

          {watch("payment_based_on") === "Partial Shift Credit" && (
            <Box
              sx={{
                border: `1px solid ${themes.miliedLightGray}`,
                p: 2,
                borderRadius: 2,
                ml: 0.8,
              }}
            >
              <Box>
                <TableContainer
                  sx={{
                    width: "100%",
                  }}
                >
                  <Table className="border">
                    <TableHead sx={tableHeaderSx}>
                      <TableRow maxWidth="xl" align="left" sx={tableRowSx}>
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Min % Completed
                        </TableCell>{" "}
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Count as Shift
                        </TableCell>{" "}
                        <TableCell
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {rows.map((row, index) => (
                      <TableBody>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.work_min_percentage}
                            onChange={(e) => {
                              const val = e.target.value;
                              const validPattern = /^\d{1,3}(\.\d{0,2})?$/;

                              if (val === "" || validPattern.test(val)) {
                                handleChange(index, "work_min_percentage", val);
                              }
                            }}
                            error={
                              rowError?.[index]?.work_min_percentage || false
                            }
                            helperText={
                              rowError[index]?.work_min_percentage
                                ? row.work_min_percentage.trim() === ""
                                  ? "Enter percentage"
                                  : index > 0 &&
                                    parseFloat(row.work_min_percentage) <=
                                      parseFloat(
                                        rows[index - 1].work_min_percentage
                                      )
                                  ? `Percentage must be greater than  ${
                                      rows[index - 1]?.work_min_percentage
                                    }`
                                  : ""
                                : ""
                            }
                            placeholder="Enter percentage"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.count_as_shift}
                            onChange={(e) => {
                              const val = e.target.value;
                              const validPattern = /^[0-9]?(\.\d{0,2})?$/;
                              if (val === "" || validPattern.test(val)) {
                                handleChange(index, "count_as_shift", val);
                              }
                            }}
                            error={rowError[index]?.count_as_shift}
                            helperText={
                              rowError[index]?.count_as_shift
                                ? row.count_as_shift.trim() === ""
                                  ? "Enter Value"
                                  : index > 0 &&
                                    parseFloat(row.count_as_shift) <=
                                      parseFloat(rows[index - 1].count_as_shift)
                                  ? `Value must be greater than  ${
                                      rows[index - 1]?.count_as_shift
                                    }`
                                  : ""
                                : ""
                            }
                            placeholder="Enter value"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            arrow
                            slotProps={tooltipStyleProps}
                            title={"Remove"}
                          >
                            <IconButton
                              onClick={() => handleRemoveRow(index)}
                              disabled={rows?.length === 1}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableBody>
                    ))}
                  </Table>
                </TableContainer>
                <Grid container>
                  <Grid item xs={10.75}></Grid>
                  <Grid item xs={1}>
                    <Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title={"Add Row"}
                    >
                      <IconButton onClick={handleAddRow}>
                        <AddCircleTwoTone />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    border: `1px solid ${themes.miliedLightGray}`,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Each additional
                    </Typography>

                    <CustomBox
                      name="additional_percentage"
                      type="percentage"
                      value={watch("additional_percentage")}
                      register={register}
                      errors={errors}
                      handleChange={(e, value) => {
                        setValue("additional_percentage", value);
                      }}
                      rules={{ required: "Enter value" }}
                      inputWidth="50px"
                    />

                    <Typography variant="body2" color="text.secondary">
                      percentage is counted as
                    </Typography>
                    <CustomBox
                      name="shift_count"
                      type="shift"
                      value={watch("shift_count")}
                      register={register}
                      errors={errors}
                      handleChange={(e, value) => {
                        setValue("shift_count", value);
                      }}
                      rules={{ required: "Enter value" }}
                      inputWidth="50px"
                    />
                    <Typography variant="body2" color="text.secondary">
                      shift
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ...infoMessageStyle, ml: 0, mr: 0 }}>
                  <PartialShiftCreditExample />
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              border: `1px solid ${themes.miliedLightGray}`,
              p: 2,
              borderRadius: 2,
              ml: 0.8,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography fontWeight="medium">
                  Payment hours calculation based on
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sets how working hours are managed when employees work shifts
                  from different grade levels.
                </Typography>
              </Box>
            </Box>
            <RadioGroup
              value={watch("is_include_sft_grade_sfts")}
              onChange={handleSftGradeChange}
            >
              {shiftGradeOptions.map((opt) => {
                const isActive = watch("is_include_sft_grade_sfts") === opt.id;
                return (
                  <Box key={opt.id} sx={{ ...cardStyle(isActive), mt: 1 }}>
                    <FormControlLabel
                      value={opt.id}
                      control={<Radio />}
                      label={
                        <Box display="flex" flexDirection="column">
                          <Typography
                            fontWeight={"medium"}
                            sx={{
                              color:
                                watch("is_include_sft_grade_sfts") === opt.id
                                  ? themes.stepActiveColor
                                  : "inherit",
                            }}
                          >
                            {opt.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {opt.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>

          <Box
            sx={{
              ml: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CustomButton actionName="Next" typeName="submit" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
